/**
 * Trainova AI — Super Admin Scenario Tenant Availability Modal
 * Enables Platform Super Admins to map and whitelist scenarios to organizations/tenants.
 */

import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import '../admin-portal.css';

export interface TenantAvailabilityModalProps {
  simulationId: string;
  simulationTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdated?: (tenantIds: string[]) => void;
}

export function TenantAvailabilityModal({
  simulationId,
  simulationTitle,
  isOpen,
  onClose,
  onUpdated,
}: TenantAvailabilityModalProps) {
  const [tenants, setTenants] = useState<Array<{ id: string; name: string; slug: string; isActive: boolean }>>([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState<Set<string>>(new Set());
  const [isAllTenants, setIsAllTenants] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    currentPage: tenantPage,
    pageSize: tenantPageSize,
    totalItems: tenantTotalItems,
    paginatedItems: paginatedTenants,
    goToPage: setTenantPage,
    setPageSize: setTenantPageSize,
  } = usePagination(tenants, 1, 20);

  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.admin.getTenantAvailability(simulationId);
        if (res && res.data) {
          setTenants(res.data.tenants || []);
          const available = res.data.availableToTenantIds || [];
          if (available.includes('*') || (available.length > 0 && available.length === res.data.tenants.length)) {
            setIsAllTenants(true);
            setSelectedTenantIds(new Set(res.data.tenants.map((t) => t.id)));
          } else {
            setIsAllTenants(false);
            setSelectedTenantIds(new Set(available));
          }
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch scenario tenant availability');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isOpen, simulationId]);

  const toggleAll = () => {
    if (isAllTenants) {
      setIsAllTenants(false);
      setSelectedTenantIds(new Set());
    } else {
      setIsAllTenants(true);
      setSelectedTenantIds(new Set(tenants.map((t) => t.id)));
    }
  };

  const toggleTenant = (id: string) => {
    const next = new Set(selectedTenantIds);
    if (next.has(id)) {
      next.delete(id);
      setIsAllTenants(false);
    } else {
      next.add(id);
      if (next.size === tenants.length) {
        setIsAllTenants(true);
      }
    }
    setSelectedTenantIds(next);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const payload = isAllTenants ? ['*'] : Array.from(selectedTenantIds);
      await api.admin.setTenantAvailability(simulationId, payload);
      setSuccessMessage('Scenario tenant availability updated successfully!');
      if (onUpdated) onUpdated(payload);
      setTimeout(() => {
        onClose();
      }, 800);
    } catch (err: any) {
      setError(err.message || 'Failed to save tenant availability');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="ad-scrim" onClick={onClose} />
      <div className="ad-modal-wrapper">
        <div className="ad-modal" style={{ width: 'min(640px, 95vw)' }}>
          {/* Header */}
          <div className="ad-mh">
            <span className="ad-si" style={{ background: 'var(--vio-soft)', color: 'var(--vio)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2" />
                <path d="M7 7h.01M17 7h.01M7 17h.01M17 17h.01M12 7v10M7 12h10" />
              </svg>
            </span>
            <div>
              <h2>Organization Whitelist · {simulationTitle}</h2>
              <p>Map which client organizations can view and assign this simulation.</p>
            </div>
            <span className="ad-x" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </span>
          </div>

          {error && (
            <div style={{ padding: '10px 24px', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: '13px', fontWeight: 600 }}>
              {error}
            </div>
          )}
          {successMessage && (
            <div style={{ padding: '10px 24px', background: 'var(--ok-soft)', color: 'var(--ok)', fontSize: '13px', fontWeight: 600 }}>
              {successMessage}
            </div>
          )}

          <div className="ad-mb1">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: isAllTenants ? 'var(--blue-soft)' : '#FAFBFE',
                borderRadius: '10px',
                border: isAllTenants ? '1.5px solid var(--blue)' : '1px solid var(--line)',
                cursor: 'pointer',
              }}
              onClick={toggleAll}
            >
              <div>
                <b style={{ color: isAllTenants ? 'var(--blue)' : 'var(--navy)' }}>All Organizations (Global Availability)</b>
                <span style={{ display: 'block', fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                  Make this simulation available to all current and future client organizations.
                </span>
              </div>
              <span className={`ad-cbx ${isAllTenants ? 'ad-y' : ''}`}>
                {isAllTenants && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m5 12 5 5 9-10" />
                  </svg>
                )}
              </span>
            </div>

            <div className="ad-tbx">
              <div className="ad-tbh">
                <b>Specific Organizations ({selectedTenantIds.size} selected)</b>
              </div>
              <div style={{ maxHeight: '280px', overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="ad-AS" style={{ minWidth: '380px' }}>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Organization</th>
                      <th>Identifier</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: 'var(--muted)' }}>
                          Loading organizations...
                        </td>
                      </tr>
                    ) : tenants.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: 'var(--muted)' }}>
                          No organizations found.
                        </td>
                      </tr>
                    ) : (
                      paginatedTenants.map((tenant) => {
                      const isSelected = isAllTenants || selectedTenantIds.has(tenant.id);
                      return (
                        <tr
                          key={tenant.id}
                          className={isSelected ? 'ad-on' : ''}
                          onClick={() => toggleTenant(tenant.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <span className={`ad-cbx ${isSelected ? 'ad-y' : ''}`}>
                              {isSelected && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="m5 12 5 5 9-10" />
                                </svg>
                              )}
                            </span>
                          </td>
                          <td>
                            <b>{tenant.name}</b>
                          </td>
                          <td>
                            <code>{tenant.slug || tenant.id}</code>
                          </td>
                          <td>
                            <span className="ad-rs ad-g">Active</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                  </tbody>
                </table>
              </div>
              {tenantTotalItems > 0 && (
                <Pagination
                  currentPage={tenantPage}
                  totalItems={tenantTotalItems}
                  pageSize={tenantPageSize}
                  onPageChange={setTenantPage}
                  onPageSizeChange={setTenantPageSize}
                  showPageSizeSelector
                  pageSizeOptions={[10, 20, 50, 100]}
                  itemLabel="organizations"
                />
              )}
            </div>
          </div>

          <div className="ad-mf">
            <span className="ad-t">
              {isAllTenants ? 'All tenants have access' : `${selectedTenantIds.size} tenant(s) selected`}
            </span>
            <div className="ad-sp">
              <span className="ad-btn" onClick={onClose}>Cancel</span>
              <button
                className="ad-btn ad-pri"
                disabled={isSaving}
                onClick={handleSave}
              >
                {isSaving ? 'Saving…' : 'Save Whitelist'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
